import { useCallback, useMemo, useState } from 'react'

import {
  IRevenueAndSobHierarchyNode,
  PRODUCT_LEVELS,
  REVENUE_AND_SOB_TOTAL_ROW_ID,
  TRevenueAndSobHierarchyOrder,
  TRevenueAndSobProductLevel,
} from 'partner360/features/financialCockpit/lib'

/** The expand/collapse state a hierarchy table's dimension columns need to draw their carets and
 *  respond to clicks. Two independent axes rather than one: a Geo node carries a caret in the Geo
 *  column for its own sub-geos and a second, unrelated one in the Product column for the product
 *  subtree nested under it. */
export interface IHierarchyControls {
  expandedPrimaryRows: ReadonlySet<string>
  expandedSecondaryRows: ReadonlySet<string>
  primaryExpandableRows: ReadonlySet<string>
  secondaryExpandableRows: ReadonlySet<string>
  togglePrimaryRow: (rowId: string) => void
  toggleSecondaryRow: (rowId: string) => void
}

export interface IHierarchyExpansion<T> {
  /** `rows` minus every node with a collapsed ancestor, in the order `rows` supplied them. */
  visibleRows: T[]
  controls: IHierarchyControls
}

export interface IHierarchyExpansionOptions {
  /** The product level the table first opens down to: every caret leading into a row at or above
   *  it starts open, so that level is on screen while its own carets stay shut. Only the
   *  single-partner Revenue & SOB table passes it — the manager tables still open at Total
   *  alone. */
  expandToLevel?: TRevenueAndSobProductLevel
}

const isGeoRow = (row: IRevenueAndSobHierarchyNode) => row.level === 'geo'
const isPartnerRow = (row: IRevenueAndSobHierarchyNode) => row.level === 'partner'

/** Whether the `parent`→`child` edge belongs to the outer (primary) axis or the inner (secondary)
 *  one, given the current `hierarchyOrder`. Both axes are always present once Geo is enabled;
 *  callers that show no Geo axis at all pin `hierarchyOrder` to `'productGeo'`, which is what makes
 *  every (product-only) edge classify as primary. Below the Partner check `child` is never the
 *  Total row either, so `!isGeoRow(child)` is exactly "is a Product row" there. */
const isPrimaryHierarchyEdge = (
  parent: IRevenueAndSobHierarchyNode,
  child: IRevenueAndSobHierarchyNode,
  hierarchyOrder: TRevenueAndSobHierarchyOrder
): boolean => {
  /** A Partner leaf hangs off the end of the Geo axis and takes the Geo column's caret — free
   *  precisely because its parent is that axis's last node, so nothing else competes for it. Which
   *  of the two sets drives that column is the whole question, hence the bare order check.
   *
   *  Needed rather than redundant: a partner's own level is `'partner'`, not `'geo'`, so without
   *  this branch it would fall through to the last clause and be classed as a secondary edge under
   *  `'geoProduct'` — drawing its caret in the Product column, where the "Offering" root already
   *  sits. */
  if (isPartnerRow(child)) {
    return hierarchyOrder === 'geoProduct'
  }

  if (parent.level === 'total') {
    return hierarchyOrder === 'geoProduct' ? isGeoRow(child) : !isGeoRow(child)
  }

  return hierarchyOrder === 'geoProduct'
    ? isGeoRow(parent) && isGeoRow(child)
    : !isGeoRow(parent) && !isGeoRow(child)
}

/** `defaults` with each of `toggledRowIds` flipped: a caret click turns its row away from however
 *  it started, and a second click turns it back. */
const flipRows = (
  defaults: ReadonlySet<string>,
  toggledRowIds: readonly string[]
): ReadonlySet<string> => {
  const expandedRows = new Set(defaults)

  toggledRowIds.forEach((rowId) => {
    if (expandedRows.has(rowId)) {
      expandedRows.delete(rowId)
    } else {
      expandedRows.add(rowId)
    }
  })

  return expandedRows
}

/** Drives the two-axis expand/collapse of a `parentRowId` hierarchy: which rows are currently
 *  visible, which have a caret on which axis, and the toggles behind those carets. Shared by the
 *  Revenue & SOB and Plan Quality tables so both nest identically — they differ only in the
 *  figures hanging off each node, which is why this is generic over anything shaped like an
 *  `IRevenueAndSobHierarchyNode`. How far a table starts open is its own call, through
 *  `expandToLevel`. */
export const useHierarchyExpansion = <T extends IRevenueAndSobHierarchyNode>(
  rows: T[],
  hierarchyOrder: TRevenueAndSobHierarchyOrder,
  { expandToLevel }: IHierarchyExpansionOptions = {}
): IHierarchyExpansion<T> => {
  /** The carets the user has clicked, each one flipping its row away from where it starts. Kept as
   *  clicks rather than as the open rows themselves, so a row that arrives after the first draw — a
   *  filter change, data that lands late — still starts at its default instead of shut. */
  const [toggledPrimaryRowIds, setToggledPrimaryRowIds] = useState<string[]>([])
  const [toggledSecondaryRowIds, setToggledSecondaryRowIds] = useState<string[]>([])

  const rowsById = useMemo(() => new Map(rows.map((row) => [row.rowId, row])), [rows])

  const {
    primaryExpandableRows,
    secondaryExpandableRows,
    defaultPrimaryRows,
    defaultSecondaryRows,
  } = useMemo(() => {
    const primaryRows = new Set<string>()
    const secondaryRows = new Set<string>()
    /** Total starts open so manager mode doesn't render empty until the user manually opens it — a
     *  harmless no-op in partner mode, where no row ever has this id (see
     *  `getRevenueAndSobHierarchyRows`). */
    const primaryDefaults = new Set<string>([REVENUE_AND_SOB_TOTAL_ROW_ID])
    const secondaryDefaults = new Set<string>()
    /** Offering down to `expandToLevel`: the product levels on screen before the first click, so
     *  the caret leading into each of them starts open. */
    const defaultVisibleLevels = new Set<string>(
      expandToLevel ? PRODUCT_LEVELS.slice(0, PRODUCT_LEVELS.indexOf(expandToLevel) + 1) : []
    )

    rows.forEach((child) => {
      const parent = child.parentRowId ? rowsById.get(child.parentRowId) : undefined

      if (!parent) {
        return
      }

      const isPrimaryEdge = isPrimaryHierarchyEdge(parent, child, hierarchyOrder)
      const expandableRows = isPrimaryEdge ? primaryRows : secondaryRows
      const defaultRows = isPrimaryEdge ? primaryDefaults : secondaryDefaults

      expandableRows.add(parent.rowId)

      if (defaultVisibleLevels.has(child.level)) {
        defaultRows.add(parent.rowId)
      }
    })

    return {
      primaryExpandableRows: primaryRows,
      secondaryExpandableRows: secondaryRows,
      defaultPrimaryRows: primaryDefaults,
      defaultSecondaryRows: secondaryDefaults,
    }
  }, [rows, rowsById, hierarchyOrder, expandToLevel])

  const expandedPrimaryRows = useMemo(
    () => flipRows(defaultPrimaryRows, toggledPrimaryRowIds),
    [defaultPrimaryRows, toggledPrimaryRowIds]
  )
  const expandedSecondaryRows = useMemo(
    () => flipRows(defaultSecondaryRows, toggledSecondaryRowIds),
    [defaultSecondaryRows, toggledSecondaryRowIds]
  )

  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        let child: T = row

        while (child.parentRowId) {
          const parent = rowsById.get(child.parentRowId)

          if (!parent) {
            return false
          }

          const expandedRows = isPrimaryHierarchyEdge(parent, child, hierarchyOrder)
            ? expandedPrimaryRows
            : expandedSecondaryRows

          if (!expandedRows.has(parent.rowId)) {
            return false
          }

          child = parent
        }

        return true
      }),
    [rows, rowsById, hierarchyOrder, expandedPrimaryRows, expandedSecondaryRows]
  )

  const togglePrimaryRow = useCallback((rowId: string) => {
    setToggledPrimaryRowIds((current) =>
      current.includes(rowId) ? current.filter((id) => id !== rowId) : [...current, rowId]
    )
  }, [])
  const toggleSecondaryRow = useCallback((rowId: string) => {
    setToggledSecondaryRowIds((current) =>
      current.includes(rowId) ? current.filter((id) => id !== rowId) : [...current, rowId]
    )
  }, [])

  const controls = useMemo(
    (): IHierarchyControls => ({
      expandedPrimaryRows,
      expandedSecondaryRows,
      primaryExpandableRows,
      secondaryExpandableRows,
      togglePrimaryRow,
      toggleSecondaryRow,
    }),
    [
      expandedPrimaryRows,
      expandedSecondaryRows,
      primaryExpandableRows,
      secondaryExpandableRows,
      togglePrimaryRow,
      toggleSecondaryRow,
    ]
  )

  return { visibleRows, controls }
}
